/* 甘寧（字興霸）
 * v0.5.2 重製：用「能」；天賦錦帆賊（擊殺回能）、Q 錦帆（掀起一波浪，甘寧或敵人在水裡就擊飛：江面、淺灘、水系範圍）、
 * W 夜襲（被動：身邊的敵將物防降低）、E 奮勇當先（往前衝刺＋攻速）、R 劫營（短冷卻的周圍斬擊＋緩速）。 */
import { SG, K, gd, landing, spMul, sigOf } from '../../core/kit.ts';
import type { Unit } from '../types.ts';
import { defineHero, hook, S, D } from '../define.ts';

const ID = 'ganning';
// 在水裡：江面、淺灘、技能鋪的水域、決水灌城、冰（水）系範圍（見 core/elements.ts SG.inWaterArea）
const inWater = (w: any, u: Unit) => SG.inWaterArea(w, u);

export default defineHero({
  id: ID, name: '甘寧', sub: '字興霸', faction: 'wu', role: '刺客', weapon: 'sword', res: 'neng',
  look: { dual: true, head: 'pony', eye: 'fierce', skin: '#d7a172', fx: '#ffe28a', c: { armor: '#7f2a24' } },
  passive: ['錦帆賊', '親手擊殺一個單位回 10 能；擊殺敵將回滿能。'],
  skills: [
    ['錦帆', 'bolt', '朝方向掀起一波浪，路徑上每個敵人受到 {70/110/150/190} 物理傷害。甘寧站在水裡（江面、淺灘、水系技能的範圍）時，浪打中的人都被擊飛 0.8 秒；否則只有站在水裡的敵人被擊飛。',
      { x: { k: 'gnSail', dmg: [70, 110, 150, 190], up: 0.8, at: 0.1 }, aim: D(750, 30), cd: [9, 8.3, 7.5, 6.7], mp: 30, free: true, el: null }],
    ['夜襲', 'passive', '被動：身邊 350 內的敵將物防 -{8/12/16/20}%。', { pv: {}, sig: 'night', shred: [8, 12, 16, 20], r: 350 }],
    ['奮勇當先', 'dash', '往前衝刺一小段，之後 4 秒攻速 +{30/45/60/75}%。', { x: { k: 'gnRush', as: [30, 45, 60, 75], dur: 4, dist: 260, at: 0.05 }, aim: D(260, 40), cd: [18, 16, 14, 12], mp: 30, el: null }],
    ['劫營', 'spin', '旋身斬擊周圍 260 內的敵人，造成 {120/170/220} 物理傷害並緩速 {40/50/60}%，2 秒內遞減。', { x: { k: 'gnSweep', dmg: [120, 170, 220], r: 260, slow: [40, 50, 60], dur: 2, at: 0.1 }, aim: S(260), cd: [10, 8.3, 6.7], mp: 30, free: true, minor: true, el: null }],
  ],
  // 專屬技能（技能資料裡的 x.k）
  kinds: {
    gnSail(u, a, X) {
      const sp = spMul(u), water = inWater(this, u); // 放的時候甘寧在不在水裡
      this.shootProj(u, { dir: a.dir, speed: 900, range: this.skillRange(a.sk, a.lvl), shape: 'wind', col: '#7fd0ff', rad: 40,
        filter: (t: Unit) => !t.structure,
        onHit: (t: Unit) => {
          this.damage(u, t, X.dmg * sp, { stagger: 0.2 });
          if (t.alive && (water || inWater(this, t))) { this.applyCC(u, t, { knockup: X.up }); this.fx({ k: 'spark', x: t.x, y: t.y - 30, col: '#7fd0ff' }); }
          return false; // 浪：貫穿整條路徑
        } });
    },
    gnRush(u, a, X) {
      const to = landing(u.x, u.y, u.x + a.dir[0] * X.dist, u.y + a.dir[1] * X.dist * K), n = 6, dx = (to[0] - u.x) / n, dy = (to[1] - u.y) / n;
      u.air = true;
      for (let j = 1; j <= n; j++) this.later(0.03 * j, () => { if (!u.alive) return; u.x += dx; u.y += dy; if (j === n) u.air = false; });
      this.addBuff(u, { id: '奮勇當先', t: X.dur, as: X.as });
      this.fx({ k: 'dust', x: u.x, y: u.y, col: u.spec.fx });
    },
    gnSweep(u, a, X) {
      const sp = spMul(u), t0 = this.t, hit: Unit[] = [];
      this.enemiesNear(u, u.x, u.y, X.r).forEach((t) => {
        if (t.structure) return;
        this.damage(u, t, X.dmg * sp, { stagger: 0.25 });
        if (t.alive) hit.push(t);
      });
      const tick = () => { // 緩速：2 秒內遞減
        const k = 1 - (this.t - t0) / X.dur;
        if (k <= 0) return;
        hit.forEach((t) => { if (t.alive) this.addBuff(t, { id: '劫營', t: 0.3, neg: true, ms: -(X.slow / 100) * k }); });
        this.later(0.25, tick);
      };
      tick();
      this.fx({ k: 'bladering', x: u.x, y: u.y, r: X.r, col: u.spec.fx });
    },
  },
  // 被動與專屬效果：hook(時間點, 順位, 處理)（時間點一覽見 core/hooks.ts）
  hooks: [
    // 夜襲（被動）：身邊的敵將物防降低
    hook('aura', 40, function () {
      for (const u of this.heroes()) {
        const g = u.alive && u.spec.id === ID ? sigOf(u, 'night') : null;
        if (!g) continue;
        for (const t of this.heroes(1 - u.team)) if (t.alive && gd(t.x, t.y, u.x, u.y) <= g.v('r')) this.addBuff(t, { id: '夜襲', t: 0.7, neg: true, armor: -Math.max(0, t.armor) * g.v('shred') / 100 });
      }
    }),
    // 錦帆賊：親手擊殺回 10 能，擊殺敵將回滿
    hook('kill.after', 30, function (src, t, was) {
      const u = src && (src.owner || src); // was 只在擊殺武將時是 true；小兵、野怪看這一下有沒有真的死
      if (!u || !u.spec || u.spec.id !== ID || !u.alive || u.team === t.team || t.alive || (t.kind === 'hero' && !was)) return;
      u.mp = t.kind === 'hero' ? u.maxMp : Math.min(u.maxMp, u.mp + 10);
    }),
  ],
  // 電腦什麼時候放這招
  ai: {
    gnSail: ({ t, d }) => !!t && !t.structure && d < 700,
    gnRush: ({ hero, d }) => hero && d > 150 && d < 420,
    gnSweep: ({ t, d }) => !!t && !t.structure && d < 230,
  },
});
